import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Select from 'components/commonComponents/select/index';

import Input from '../Input';

import { ICONS } from '../../../../../assets/icons';
import { cn } from '../../../../../helper/cn';
import { flowsList } from '../../../../channels/api/Api';
import { useAspSelector } from '../../../../../test/jest-redux-hooks';
import NotificationContext from '../../../../../providers/NotificationProvider';
import { showErrorToast } from '../../../../../components/commonComponents/gxAntdToast/toastUtils';

const resolveFlowIdentifier = (flow) => {
  if (!flow) return '';
  return String(flow?.id ?? flow?.flow_id ?? flow?.uuid ?? '').trim();
};

const formatFlowOption = (flow) => {
  const identifier = resolveFlowIdentifier(flow);

  return {
    label: flow?.name ?? (identifier ? `Flow ${identifier}` : 'Unnamed Flow'),
    value: identifier,
    identifier,
    data: flow || null,
  };
};

const FLOW_CTA_MAX_LENGTH = 20;

const FlowMessage = ({ node, onChange, allowToEditFlow }) => {
  const { currentBrand } = useAspSelector((state) => state.app);
  const notification = useContext(NotificationContext);

  const [flowOptions, setFlowOptions] = useState([]);
  const [isFetchingFlows, setIsFetchingFlows] = useState(false);
  const [flowFetchError, setFlowFetchError] = useState(null);
  const lastFetchedFlowBrandRef = useRef(null);

  const flowParameters = useMemo(() => {
    if (node?.type !== 'FLOW') return {};
    return node?.data?.interactive?.action?.parameters || {};
  }, [node?.data?.interactive?.action?.parameters, node?.type]);

  const selectedFlowIdentifier = useMemo(() => {
    if (node?.type !== 'FLOW') return '';

    const identifier =
      flowParameters?.flow_id ||
      flowParameters?.flow_identifier ||
      flowParameters?.flow_name ||
      '';

    return identifier ? String(identifier) : '';
  }, [
    flowParameters?.flow_id,
    flowParameters?.flow_identifier,
    flowParameters?.flow_name,
    node?.type,
  ]);

  const selectedFlowOption = useMemo(() => {
    if (node?.type !== 'FLOW') return null;
    if (!selectedFlowIdentifier) return null;

    const matched = flowOptions.find(
      (option) => option.identifier === selectedFlowIdentifier
    );

    if (matched) return matched;

    const fallbackLabel =
      flowParameters?.flow_name ||
      (selectedFlowIdentifier ? `Flow ${selectedFlowIdentifier}` : null);

    if (!fallbackLabel) return null;

    return {
      label: fallbackLabel,
      value: selectedFlowIdentifier,
      identifier: selectedFlowIdentifier,
      data: null,
    };
  }, [flowOptions, flowParameters?.flow_name, node?.type, selectedFlowIdentifier]);

  const isFlowCtaValid = useMemo(() => {
    if (node?.type !== 'FLOW') return true;
    const text = (flowParameters?.flow_cta || '').trim();
    return Boolean(text) && text.length <= FLOW_CTA_MAX_LENGTH;
  }, [flowParameters?.flow_cta, node?.type]);

  const hasSelectedFlow = useMemo(() => {
    if (node?.type !== 'FLOW') return true;
    return Boolean((selectedFlowIdentifier || '').trim());
  }, [node?.type, selectedFlowIdentifier]);

  const updateFlowParameters = useCallback(
    (updates = {}) => {
      if (!allowToEditFlow || node?.type !== 'FLOW') return;

      const currentInteractive = node?.data?.interactive || {};
      const currentAction = currentInteractive?.action || {};
      const currentParameters = currentAction?.parameters || {};

      const nextParameters = {
        flow_message_version: currentParameters?.flow_message_version || '3',
        ...currentParameters,
        ...updates,
      };

      const updatedInteractive = {
        ...currentInteractive,
        type: 'flow',
        action: {
          ...currentAction,
          name: 'flow',
          parameters: nextParameters,
        },
      };

      onChange({
        type: 'UPDATE_NODE',
        id: node?.uniq_id,
        primaryKey: 'data',
        key: 'interactive',
        data: updatedInteractive,
      });
    },
    [allowToEditFlow, node, onChange]
  );

  const handleFlowOptionChange = useCallback(
    (option) => {
      if (!allowToEditFlow || node?.type !== 'FLOW') return;

      const flowData = option?.data || null;

      if (flowData) {
        const identifier = resolveFlowIdentifier(flowData);
        updateFlowParameters({
          flow_name: flowData?.name ?? option?.label ?? '',
          flow_id: flowData?.flow_id ?? '',
          flow_identifier: identifier,
          flow_uuid: flowData?.uuid ?? '',
        });
      } else if (option) {
        updateFlowParameters({
          flow_name: option?.label ?? option?.value ?? '',
          flow_id: '',
          flow_identifier: '',
          flow_uuid: '',
        });
      }
    },
    [allowToEditFlow, node?.type, updateFlowParameters]
  );

  const handleFlowCtaChange = useCallback(
    (value) => {
      if (!allowToEditFlow || node?.type !== 'FLOW') return;
      updateFlowParameters({ flow_cta: value });
    },
    [allowToEditFlow, node?.type, updateFlowParameters]
  );

  const handleFlowClear = useCallback(() => {
    if (!allowToEditFlow || node?.type !== 'FLOW') return;

    updateFlowParameters({
      flow_name: '',
      flow_id: '',
      flow_identifier: '',
      flow_uuid: '',
    });
  }, [allowToEditFlow, node?.type, updateFlowParameters]);

  const fetchPublishedFlows = useCallback(async () => {
    if (node?.type !== 'FLOW') return;

    const brandId = currentBrand?.brand_id;
    if (!brandId) return;

    setIsFetchingFlows(true);
    setFlowFetchError(null);

    try {
      const response = await flowsList(brandId, {
        status: 'PUBLISHED',
        limit: 200,
      });
      const flows = response?.data?.flows || [];
      setFlowOptions(flows.map(formatFlowOption));
      lastFetchedFlowBrandRef.current = brandId;
    } catch (error) {
      console.error('Failed to fetch published flows:', error);
      setFlowFetchError('Unable to load published flows. Please try again.');
      showErrorToast(
        notification,
        'Unable to load published flows. Please try again.'
      );
      lastFetchedFlowBrandRef.current = brandId;
    } finally {
      setIsFetchingFlows(false);
    }
  }, [currentBrand?.brand_id, node?.type, notification]);

  useEffect(() => {
    if (node?.type !== 'FLOW') return;

    if (!flowParameters?.flow_message_version) {
      updateFlowParameters({ flow_message_version: '3' });
    }
  }, [flowParameters?.flow_message_version, node?.type, updateFlowParameters]);

  useEffect(() => {
    if (node?.type !== 'FLOW') {
      if (flowOptions.length > 0) setFlowOptions([]);
      setFlowFetchError(null);
      setIsFetchingFlows(false);
      lastFetchedFlowBrandRef.current = null;
      return;
    }

    const brandId = currentBrand?.brand_id;

    if (!brandId) {
      if (flowOptions.length > 0) setFlowOptions([]);
      lastFetchedFlowBrandRef.current = null;
      return;
    }

    if (lastFetchedFlowBrandRef.current !== brandId && flowOptions.length > 0) {
      setFlowOptions([]);
    }

    if (lastFetchedFlowBrandRef.current !== brandId) {
      setFlowFetchError(null);
    }

    const alreadyFetched =
      lastFetchedFlowBrandRef.current === brandId && flowOptions.length > 0;

    if (!alreadyFetched && !isFetchingFlows) {
      fetchPublishedFlows();
    }
  }, [
    currentBrand?.brand_id,
    fetchPublishedFlows,
    flowOptions.length,
    isFetchingFlows,
    node?.type,
  ]);

  if (node?.type !== 'FLOW') return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <p className="text-xs weight-semibold text-[#2D3036]">Select Flow</p>
        <Select
          options={flowOptions}
          value={selectedFlowOption}
          placeholder={
            isFetchingFlows
              ? 'Loading published flows...'
              : flowOptions.length > 0
              ? 'Choose a published flow'
              : 'No published flows found'
          }
          allow={allowToEditFlow && !isFetchingFlows}
          dropDownIcon={ICONS?.SelectDropdown}
          onChange={handleFlowOptionChange}
          rootClassName={cn(
            'min-h-[2.5rem]',
            (!allowToEditFlow || isFetchingFlows) && 'opacity-70 cursor-not-allowed'
          )}
        />
        {flowFetchError && (
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-red-500">{flowFetchError}</p>
            {allowToEditFlow && (
              <button
                type="button"
                className="text-xs text-[#6940F2]"
                onClick={fetchPublishedFlows}
                disabled={isFetchingFlows}
              >
                Retry
              </button>
            )}
          </div>
        )}
        {!hasSelectedFlow && !isFetchingFlows && !flowFetchError && (
          <p className="text-xs text-red-500 mt-1">
            Selecting a flow is required
          </p>
        )}
        {selectedFlowIdentifier && allowToEditFlow && (
          <button
            type="button"
            className="text-xs text-[#6940F2] mt-1 w-fit"
            onClick={handleFlowClear}
            disabled={isFetchingFlows}
          >
            Clear selection
          </button>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <p className="text-xs weight-semibold text-[#2D3036]">Button Text</p>
        <Input
          value={flowParameters?.flow_cta || ''}
          limit={FLOW_CTA_MAX_LENGTH}
          onInputChange={handleFlowCtaChange}
          placeholder="e.g. Book now"
          disabled={!allowToEditFlow}
        />
        {!isFlowCtaValid && (
          <p className="text-xs text-red-500 mt-1">
            Button text is required (max {FLOW_CTA_MAX_LENGTH} characters)
          </p>
        )}
      </div>
    </div>
  );
};

export default FlowMessage;
